import { getImageData } from "./CanvasSurface";
import { PinPath, Point, Shape } from "./Types";
import { calculateResizedDimensions } from "./utils/shapeUtils";
import { drawPathWithClear } from "./utils/pathDrawing";

export class Target {
    private readonly targetImageData: ImageData; // Image data composited on white for display
    private readonly targetValue: number[]; // Pre-computed grayscale values
    private readonly alpha: number[]; // Mask values: 1 inside, 0 outside
    private readonly workCanvas: HTMLCanvasElement;
    private readonly targetCanvas: HTMLCanvasElement;
    public readonly width: number;
    public readonly height: number;

    constructor(image: HTMLImageElement, maxDimension: number = 400, invertImage: boolean = false, shape: Shape = 'ellipse', maskSize: number = 100) {
        // Resize to maxDimension on the maximum dimension while maintaining aspect ratio
        const { width, height } = calculateResizedDimensions(image.width, image.height, maxDimension);
        this.width = width;
        this.height = height;
        
        // Create target canvas internally
        this.targetCanvas = document.createElement('canvas');
        this.targetCanvas.width = this.width;
        this.targetCanvas.height = this.height;
        
        // Draw the resized target image on opaque white background
        const targetCtx = this.targetCanvas.getContext("2d", { willReadFrequently: true })!;
        // First paint opaque white background to ignore original image alpha
        targetCtx.fillStyle = "white";
        targetCtx.fillRect(0, 0, this.width, this.height);
        // Then draw the image on top
        targetCtx.drawImage(image, 0, 0, this.width, this.height);
        
        this.targetImageData = getImageData(this.targetCanvas);
        
        // Convert to grayscale and pre-compute target values and mask
        const pixelCount = (this.width * this.height);
        this.targetValue = new Array(pixelCount);
        this.alpha = new Array(pixelCount);
        
        // Calculate mask parameters
        const centerX = this.width / 2;
        const centerY = this.height / 2;
        const scale = maskSize / 100;
        
        for (let pixelIdx = 0; pixelIdx < pixelCount; pixelIdx++) {
            const i = pixelIdx * 4;
            const r = this.targetImageData.data[i];
            const g = this.targetImageData.data[i + 1];
            const b = this.targetImageData.data[i + 2];
            
            // Convert to grayscale using standard luminance formula
            const gray = 0.299 * r + 0.587 * g + 0.114 * b;
            
            // Invert if requested
            this.targetValue[pixelIdx] = invertImage ? 255 - gray : gray;
            
            // Calculate mask value based on position
            const x = pixelIdx % this.width;
            const y = Math.floor(pixelIdx / this.width);
            
            let insideMask = false;
            if (shape === 'rectangle') {
                const halfWidth = (this.width * scale) / 2;
                const halfHeight = (this.height * scale) / 2;
                insideMask = Math.abs(x - centerX) <= halfWidth && Math.abs(y - centerY) <= halfHeight;
            } else {
                // Ellipse shape
                const radiusX = (this.width / 2) * scale;
                const radiusY = (this.height / 2) * scale;
                const dx = (x - centerX) / radiusX;
                const dy = (y - centerY) / radiusY;
                insideMask = (dx * dx + dy * dy) <= 1;
            }
            
            this.alpha[pixelIdx] = insideMask ? 1 : 0;
        }
        
        // Create work canvas internally
        this.workCanvas = document.createElement('canvas');
        this.workCanvas.width = this.width;
        this.workCanvas.height = this.height;
    }

    public computePathDiff(path: PinPath, points: Point[]): number {
        const ctx = this.workCanvas.getContext("2d", { willReadFrequently: true })!;
        drawPathWithClear(ctx, path, points, this.width, this.height);
        const workImageData = ctx.getImageData(0, 0, this.width, this.height);
        return this.computeDiff(workImageData);
    }

    private computeDiff(actual: ImageData): number {
        const DIVISOR = Math.pow(255, 6);
        let sum = 0;
        const pixelCount = this.width * this.height;

        for (let pixelIdx = 0; pixelIdx < pixelCount; pixelIdx++) {
            // Skip pixels outside the mask
            if (this.alpha[pixelIdx] === 0) {
                continue;
            }
            
            const i = pixelIdx * 4;
            
            // Actual image is already grayscale (black lines on white)
            // so we can just use the red channel
            const gray1 = actual.data[i];
            
            // Compare grayscale values
            const gray2 = this.targetValue[pixelIdx];
            const diff = gray1 - gray2;
            const diff2 = diff * diff;
            sum += diff2 * diff2 * diff2;
        }
        
        // Divide by 255⁶ at the end
        return sum / DIVISOR;
    }

    public getTargetImageData(): ImageData {
        return this.targetImageData;
    }
}
